import React, { useCallback, useEffect, useState } from "react";
import type { CameraGridItem, OccupancyBand } from "../../services/peopleDistributionTypes";
import { formatSecondsShort } from "../../services/peopleDistributionTypes";

type CameraGridCurrentProps = {
  cameras: CameraGridItem[];
  totalCount: number;
  onLoadMore: () => Promise<CameraGridItem[]>;
};

const BAND_STYLES: Record<OccupancyBand, string> = {
  high: "text-red-500",
  mid: "text-amber-500",
  low: "text-emerald-500",
};

const BAND_LABELS: Record<OccupancyBand, string> = {
  high: "High",
  mid: "Mid",
  low: "Low",
};

const CLASS_BREAKDOWN_BADGES = [
  {
    field: "studentCount" as const,
    label: "stu",
    className:
      "rounded-full bg-teal-500/15 px-2 py-0.5 text-[10px] font-medium text-teal-600 dark:text-teal-300",
  },
  {
    field: "staffCount" as const,
    label: "staff",
    className:
      "rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300",
  },
  {
    field: "securityGuardCount" as const,
    label: "guard",
    className:
      "rounded-full bg-orange-500/15 px-2 py-0.5 text-[10px] font-medium text-orange-700 dark:text-orange-300",
  },
  {
    field: "personCount" as const,
    label: "person",
    className:
      "rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300",
  },
];

function getPeakRatioTooltip(camera: CameraGridItem): string | undefined {
  const current = camera.current ?? 0;
  const peak = camera.peakToday ?? 0;
  if (current <= 0 || peak <= 0) return undefined;
  return `${Math.round((current / peak) * 100)}% of peak today`;
}

function CameraCard({ camera }: { camera: CameraGridItem }) {
  const band: OccupancyBand = camera.occupancyBand ?? "low";
  const bandTooltip = getPeakRatioTooltip(camera);
  const hasCurrent = camera.current != null;
  const hasDwell = camera.dwell != null && camera.dwell > 0;
  const breakdownBadges = CLASS_BREAKDOWN_BADGES.filter(
    ({ field }) => (camera[field] ?? 0) > 0
  );
  const aliasName = camera.cameraName?.trim() || camera.cameraId;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700/60 dark:bg-[#121622]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-slate-900 dark:text-white">
            {aliasName}
          </p>
          {camera.locationName ? (
            <p className="truncate text-[10px] text-slate-500 dark:text-slate-400">
              {camera.locationName}
            </p>
          ) : null}
        </div>
        <span
          className={`flex shrink-0 items-center gap-1 text-[10px] font-medium ${BAND_STYLES[band]}`}
          title={bandTooltip}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {BAND_LABELS[band]}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Current</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {hasCurrent ? Math.round(camera.current!) : "—"}
          </p>
        </div>
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Peak</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {camera.peakToday > 0 ? Math.round(camera.peakToday) : "—"}
          </p>
        </div>
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Dwell</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {hasDwell ? formatSecondsShort(camera.dwell!) : "—"}
          </p>
        </div>
      </div>

      {breakdownBadges.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {breakdownBadges.map(({ field, label, className }) => (
            <span key={field} className={className}>
              {camera[field]} {label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const CameraGridCurrent: React.FC<CameraGridCurrentProps> = ({
  cameras,
  totalCount,
  onLoadMore,
}) => {
  const [expandedCameras, setExpandedCameras] = useState<CameraGridItem[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setExpandedCameras([]);
    setIsExpanded(false);
    setLoadError(null);
  }, [cameras, totalCount]);

  const visibleCameras = isExpanded ? [...cameras, ...expandedCameras] : cameras;
  const remainingCount = Math.max(0, totalCount - cameras.length);

  const handleSeeMore = useCallback(async () => {
    if (isLoadingMore || remainingCount <= 0) return;
    setIsLoadingMore(true);
    setLoadError(null);
    try {
      const more = await onLoadMore();
      setExpandedCameras(more);
      setIsExpanded(true);
    } catch {
      setLoadError("Could not load more cameras.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, onLoadMore, remainingCount]);

  const handleShowLess = useCallback(() => {
    setIsExpanded(false);
    setExpandedCameras([]);
    setLoadError(null);
  }, []);

  if (!cameras.length && totalCount === 0) {
    return (
      <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No cameras available for the selected filters.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-400">⬡</span>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Camera grid — current</h3>
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
              {totalCount} cameras
            </span>
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Band = current vs this camera&apos;s peak today (High &gt;80%, Mid 40–80%, Low ≤40%)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {visibleCameras.map((camera) => (
          <CameraCard key={camera.cameraId} camera={camera} />
        ))}
      </div>

      {loadError ? (
        <p className="mt-3 text-xs text-red-500 dark:text-red-400">{loadError}</p>
      ) : null}

      {!isExpanded && remainingCount > 0 ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleSeeMore}
            disabled={isLoadingMore}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-[#121622] dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {isLoadingMore ? "Loading…" : `See more (${remainingCount} cameras)`}
          </button>
        </div>
      ) : null}

      {isExpanded && expandedCameras.length > 0 ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleShowLess}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-[#121622] dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Show less
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default CameraGridCurrent;
